import React, { useState } from "react";
import { Check, Lock, Clock, DollarSign } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function EscrowTimeline({ escrow, onUpdated }) {
  const [busy, setBusy] = useState(false);
  if (!escrow) return null;
  const ms = escrow.milestones && escrow.milestones.length > 0 ? escrow.milestones : null;

  const releaseMilestone = async (idx) => {
    if (!confirm(`Release ${ms[idx].name} ($${ms[idx].amount_usd.toLocaleString()})?`)) return;
    setBusy(true);
    try {
      await api.post(`/escrow/${escrow.escrow_id}/milestone/${idx}/release`);
      toast.success(`${ms[idx].name} released`);
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Release failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-[#0a0a0a] border border-[#262626] rounded-xl p-6" data-testid="escrow-timeline">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="overline">Escrow {ms && ms.length > 1 ? "· MICE Milestones" : "· 10% Hold"}</p>
          <p className="font-display text-2xl font-bold text-white mt-1">
            ${Number(escrow.total_usd).toLocaleString()}
            <span className="text-[#737373] text-sm font-normal ml-2">total</span>
          </p>
          {escrow.platform_fee_usd > 0 && (
            <p className="text-xs font-mono text-[#737373] mt-1">
              Platform fee · ${escrow.platform_fee_usd} ({escrow.pax} pax)
            </p>
          )}
        </div>
        <span
          className={`text-xs font-mono uppercase tracking-widest px-3 py-1 rounded ${
            escrow.status === "released"
              ? "text-green-400 border border-green-400/30"
              : "text-blue-400 border border-blue-400/30"
          }`}
        >
          {escrow.status}
        </span>
      </div>

      {ms ? (
        <ol className="space-y-3">
          {ms.map((m, i) => {
            const isReleased = m.status === "released";
            const isHeld = m.status === "held";
            return (
              <li
                key={i}
                className={`relative flex items-center gap-3 p-3 rounded-md border ${
                  isReleased ? "border-green-400/30 bg-green-400/5"
                  : isHeld ? "border-brand/40 bg-brand/5"
                  : "border-[#262626] bg-[#080808]"
                }`}
                data-testid={`milestone-${i}`}
              >
                <span className={`w-7 h-7 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  isReleased ? "bg-green-400 border-green-400 text-black"
                  : isHeld ? "bg-brand border-brand text-white"
                  : "bg-[#0a0a0a] border-[#262626] text-[#737373]"
                }`}>
                  {isReleased ? <Check className="w-3.5 h-3.5" />
                    : isHeld ? <DollarSign className="w-3.5 h-3.5" />
                    : <Lock className="w-3.5 h-3.5" />}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${isReleased || isHeld ? "text-white" : "text-[#737373]"}`}>
                    {m.name} <span className="font-mono text-[#a3a3a3] ml-1">· {m.percent}%</span>
                  </p>
                  <p className="text-xs font-mono text-[#737373]">
                    ${Number(m.amount_usd).toLocaleString()}{m.ts && ` · ${new Date(m.ts).toLocaleDateString()}`}
                  </p>
                </div>
                {isHeld && (
                  <button
                    onClick={() => releaseMilestone(i)}
                    disabled={busy}
                    className="text-xs font-bold uppercase tracking-widest bg-brand text-white px-3 py-1.5 rounded-md hover:bg-[#FF5722]"
                    data-testid={`release-milestone-${i}`}
                  >
                    Release
                  </button>
                )}
              </li>
            );
          })}
        </ol>
      ) : (
        <ol className="relative pl-6 space-y-5">
          <span className="absolute left-2 top-2 bottom-2 w-px bg-gradient-to-b from-[#FF4500] to-[#262626]" />
          {escrow.timeline?.map((s, i) => (
            <li key={i} className="relative flex items-start gap-3">
              <span className={`absolute -left-6 top-0 w-4 h-4 rounded-full border-2 ${s.done ? "bg-brand border-brand" : "bg-[#0a0a0a] border-[#262626]"}`} />
              <div className="flex-1">
                <p className={`text-sm font-medium ${s.done ? "text-white" : "text-[#737373]"}`}>{s.step}</p>
                {s.ts && <p className="text-xs font-mono text-[#737373]">{new Date(s.ts).toLocaleString()}</p>}
              </div>
              {s.done ? <Check className="w-4 h-4 text-brand" /> : <Clock className="w-4 h-4 text-[#737373]" />}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
